import React, { useState } from "react";
import styled from "styled-components";
import { roundToDigits, shorten } from "@backpacker/number";
import {
  Color,
  FontWeight,
  ZIndexes,
  borderRadius,
  content,
  ellipsis,
  form,
  transparentize,
} from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import { sanitizeHTML } from "@tootools/dom";
import { parse } from "@tootools/emoji";
import { isValid, percent } from "@tootools/number";
import Evolution from "@ui-kit/format/evolution";
import Icon from "@ui-kit/icon";
import Portrait from "@ui-kit/identity/portrait";
import Tooltip from "@ui-kit/tooltip";
import { Gauge } from "./model";

export const UNITS = [
  "percentage",
  "total",
  "rating",
  "penetrationRate",
] as const;
type Unit = typeof UNITS[number];

export interface IProps {
  color: string;
  gauge: Gauge;
  index: number;
  locale: Locale;
  mainUnit?: Unit;
  className?: string;
  showAvatar?: boolean;
  showIndex?: boolean;
  showTrackWhenSuperNarrow?: boolean;
  width: number;
  secondaryValue?: "total" | "percentage" | "evolution" | "none";
  total: number;
  maxCount: number;
  evolutionTooltip?: React.ReactNode;
  isNarrow: boolean;
  isHidden?: boolean;
  isHideable?: boolean;
}

const Root = styled.div<{ isNarrow: boolean; avatar?: boolean }>`
  cursor: pointer;
  font-size: 14px;
  color: ${content};
  height: 54px;
  display: flex;
  align-items: ${({ isNarrow }) => (isNarrow ? "stretch" : "center")};
  font-weight: ${FontWeight.Normal};
  &:hover {
    background-color: ${(props) =>
      transparentize(0.85, form(props, "primary"))};
  }
`;

const RootPhantom = styled.div`
  height: 54px;
`;

const GaugeWrapper = styled.div<{ isNarrow: boolean }>`
  display: flex;
  flex: 1 0;
  align-items: ${(props) => !props.isNarrow && "center"};
`;

const GaugeContainer = styled.div<{ isNarrow: boolean }>`
  flex: 1 0;
  display: flex;
  flex-direction: ${(props) => (props.isNarrow ? "column" : "row")};
  align-items: ${(props) => !props.isNarrow && "center"};
  align-self: center;
  width: 1px;
`;

const Index = styled.div<{ isNarrow: boolean }>`
  flex: 0 0 36px;
  padding-left: 12px;
  align-self: ${(props) => (props.isNarrow ? "flex-start" : "center")};
  padding-top: ${(props) => props.isNarrow && "8px"};
  color: ${(props) => content(props, "secondary")};
  margin-top: ${({ isNarrow }) => isNarrow && "3px"};
`;

const StyledPortrait = styled(Portrait)`
  margin-right: 8px;
`;

const Title = styled.div<{
  showAvatar?: boolean;
  isNarrow?: boolean;
  isHideable?: boolean;
}>`
  font-weight: ${FontWeight.Normal};
  ${(props) =>
    ellipsis(
      props.isNarrow
        ? null
        : `calc(180px - ${
            props.showAvatar ? "50" : props.isHideable ? "24" : "0"
          }px)`
    )};
  vertical-align: ${(props) => !props.isNarrow && "middle"};
  align-self: ${(props) => !props.isNarrow && "center"};
  flex-basis: ${(props) => (props.isNarrow ? "auto" : "180px")};
  margin-right: ${(props) => props.isNarrow && "8px"};
`;

const Track = styled.div<{ isRating: boolean }>`
  height: 10px;
  ${borderRadius("right", 10)};
  ${(props) => props.isRating && borderRadius("left", 10)};
  background-color: ${(props) => props.isRating && content(props, "tertiary")};
  flex: 1 0 auto;
`;

const Indicator = styled.div<{
  percentage: number;
  color: string;
  isHidden?: boolean;
}>`
  background-color: ${(props) =>
    props.isHidden
      ? transparentize(0.7, props.color ? props.color : content(props))
      : props.color || content(props)};
  height: 100%;
  border-radius: inherit;
  width: ${(props) => `${props.percentage}%`};
  min-width: ${(props) => props.percentage > 0 && "10px"};
`;

const Number = styled.div<{
  isNarrow: boolean;
  isSuperNarrow: boolean;
  isHidden: boolean;
}>`
  flex: 0 0 ${(props) => (props.isSuperNarrow ? 30 : 60)}px;
  text-align: right;
  padding-bottom: ${(props) => props.isNarrow && "4px"};
  align-self: ${(props) => props.isNarrow && "flex-end"};
  opacity: ${({ isHidden }) => isHidden && 0.8};
`;

const SecondaryNumber = styled(Number)`
  color: ${(props) => content(props, "secondary")};
`;

const BracketNumber = styled.div<{ isNarrow: boolean; isHidden }>`
  flex: 0 0 60px;
  text-align: right;
  padding-bottom: ${(props) => props.isNarrow && "4px"};
  align-self: ${(props) => (props.isNarrow ? "flex-end" : "center")};
  color: ${(props) => content(props, "secondary")};
  opacity: ${({ isHidden }) => isHidden && 0.8};
`;

// we need to position the eye icon in absolute position else, it will blink on hover
const Eye = styled.div`
  width: 24px;
  height: 24px;
  margin-right: 8px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledIcon = styled(Icon)<{ isHidden?: boolean }>`
  position: absolute;
  border-radius: 50%;
  color: ${Color.LightSeaGreen};
  z-index: ${ZIndexes.AboveContent};
  pointer-events: none;
`;

const StyledEvolution = styled(Evolution)<{
  isNarrow: boolean;
  isHidden: boolean;
  evolution: number;
}>`
  flex: 0 0 60px;
  font-size: inherit;
  text-align: right;
  overflow: hidden;
  align-self: ${(props) => props.isNarrow && "flex-end"};
  padding-bottom: ${(props) => props.isNarrow && "4px"};
  font-weight: ${FontWeight.Normal};
  color: ${(props) =>
    isValid(props.evolution) ? null : content(props, "secondary")};
  opacity: ${({ isHidden }) => isHidden && 0.8};
`;

const RatingStar = styled.div<{ isNarrow: boolean }>`
  margin: 0 0 4px 4px;
  padding-bottom: ${(props) => props.isNarrow && "2px"};
  align-self: ${(props) => props.isNarrow && "flex-end"};
`;

const SecondaryPercent = ({
  isNarrow,
  isSuperNarrow,
  isHidden,
  locale,
  percentage,
}) => (
  <SecondaryNumber
    isNarrow={isNarrow}
    isSuperNarrow={isSuperNarrow}
    isHidden={isHidden}
  >
    {shorten(locale, roundToDigits(percentage, 4), 3, false, 1)}%
  </SecondaryNumber>
);

const SecondaryTotal = ({
  isNarrow,
  isSuperNarrow,
  isHidden,
  locale,
  count,
}) => (
  <SecondaryNumber
    isNarrow={isNarrow}
    isSuperNarrow={isSuperNarrow}
    isHidden={isHidden}
  >
    {shorten(locale, count)}
  </SecondaryNumber>
);

const Delta = ({ isNarrow, isHidden, locale, evolution, percentagePoint }) => (
  <StyledEvolution
    isNarrow={isNarrow}
    isHidden={isHidden}
    locale={locale}
    evolution={evolution}
    percentagePoint={percentagePoint}
  />
);

const EvolutionPercentage = ({
  secondaryValue,
  delta,
  secondaryPercent,
  secondaryTotal,
  mainUnit,
}: {
  secondaryValue?: "total" | "percentage" | "evolution" | "none";
  delta: React.ReactNode;
  secondaryPercent: React.ReactNode;
  secondaryTotal: React.ReactNode;
  mainUnit: Unit;
}) => (
  <>
    {secondaryValue === "evolution"
      ? delta
      : secondaryValue === "percentage" && mainUnit === "percentage"
      ? secondaryTotal
      : secondaryValue === "total" && mainUnit === "total"
      ? secondaryPercent
      : secondaryValue === "percentage"
      ? secondaryPercent
      : secondaryValue === "total"
      ? secondaryTotal
      : null}
  </>
);

const BracketValue = ({
  value,
  isNarrow,
  isHidden,
}: {
  value: string;
  isNarrow: boolean;
  isHidden: boolean;
}) => (
  <BracketNumber
    isNarrow={isNarrow}
    isHidden={isHidden}
  >{`(${value})`}</BracketNumber>
);

const StyledEvolutionPercentage = styled(EvolutionPercentage)<{
  isNarrow: boolean;
}>`
  display: flex;
  align-self: ${(props) => (props.isNarrow ? "flex-end" : "center")};
  flex: 0 0 60px;
`;

const isRatingUnit = (mainUnit: Unit) => mainUnit === "rating";

const getDisplayedValue = (percentage, count, mainUnit, locale) => {
  if (isRatingUnit(mainUnit)) {
    return shorten(locale, (count / 100) * 5, 2);
  }
  return mainUnit === "percentage"
    ? shorten(locale, roundToDigits(percentage, 4), 3, false, 1) + "%"
    : shorten(locale, count);
};
const getDisplayedBracketValue = (percentage, count, mainUnit, locale) => {
  return mainUnit === "total"
    ? shorten(locale, roundToDigits(percentage, 4), 3, false, 1) + "%"
    : mainUnit === "percentage" || mainUnit === "rating"
    ? shorten(locale, count)
    : null;
};

const HorizontalBarChartGauge = ({
  gauge,
  color,
  index,
  locale,
  mainUnit = "total",
  className,
  showAvatar = false,
  showIndex = true,
  showTrackWhenSuperNarrow = true,
  width,
  secondaryValue = "percentage",
  total,
  maxCount,
  evolutionTooltip,
  isHidden,
  isHideable,
  isNarrow = width < 450,
}: IProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const percentage = percent(gauge.count, total);
  const isSuperNarrow = width < 300;
  if (gauge.isPhantom) {
    return <RootPhantom />;
  }
  const isRating = isRatingUnit(mainUnit);
  return (
    <Root
      isNarrow={isNarrow}
      className={className}
      avatar={showAvatar}
      onMouseOver={() => setIsHovered(true)}
      onMouseOut={() => setIsHovered(false)}
    >
      {showIndex && <Index isNarrow={isNarrow}>{index}</Index>}
      {!isNarrow && showAvatar && (
        <StyledPortrait size={30} picture={gauge.avatar} />
      )}
      <GaugeWrapper isNarrow={isNarrow}>
        <GaugeContainer isNarrow={isNarrow}>
          <Title
            isNarrow={isNarrow}
            dangerouslySetInnerHTML={sanitizeHTML(parse(gauge.label))}
            isHideable={isHideable}
          />
          {isHideable && (
            <Eye>
              {isHidden && !isHovered && (
                <StyledIcon glyph="eye-crossed" size={16} />
              )}
              {isHovered && (
                <StyledIcon
                  glyph={isHidden ? "eye" : "eye-crossed"}
                  size={16}
                />
              )}
            </Eye>
          )}
          {(!isSuperNarrow || showTrackWhenSuperNarrow) && (
            <Track isRating={isRating}>
              <Indicator
                percentage={percent(
                  gauge.count,
                  isRatingUnit(mainUnit) ? 100 : maxCount
                )}
                color={color}
                isHidden={isHidden}
              />
            </Track>
          )}
        </GaugeContainer>
        <Number
          isNarrow={isNarrow}
          isSuperNarrow={isSuperNarrow}
          isHidden={isHidden}
        >
          {getDisplayedValue(percentage, gauge.count, mainUnit, locale)}
        </Number>
        {isRatingUnit(mainUnit) && (
          <RatingStar isNarrow={isNarrow}>
            <Icon glyph="star-filled" size="xs" />
          </RatingStar>
        )}
      </GaugeWrapper>
      {evolutionTooltip && secondaryValue === "evolution" ? (
        <Tooltip tooltip={evolutionTooltip}>
          <StyledEvolutionPercentage
            isNarrow={isNarrow}
            secondaryValue={secondaryValue}
            mainUnit={mainUnit}
            delta={
              <Delta
                isNarrow={isNarrow}
                isHidden={isHidden}
                locale={locale}
                evolution={gauge.evolution}
                percentagePoint={mainUnit === "percentage"}
              />
            }
            secondaryPercent={
              <SecondaryPercent
                isNarrow={isNarrow}
                isSuperNarrow={isSuperNarrow}
                isHidden={isHidden}
                locale={locale}
                percentage={percentage}
              />
            }
            secondaryTotal={
              <SecondaryTotal
                locale={locale}
                isNarrow={isNarrow}
                isSuperNarrow={isSuperNarrow}
                isHidden={isHidden}
                count={gauge.count}
              />
            }
          />
        </Tooltip>
      ) : (
        <EvolutionPercentage
          secondaryValue={isRatingUnit(mainUnit) ? "none" : secondaryValue}
          mainUnit={mainUnit}
          delta={
            <Delta
              isNarrow={isNarrow}
              isHidden={isHidden}
              locale={locale}
              evolution={gauge.evolution}
              percentagePoint={mainUnit === "percentage"}
            />
          }
          secondaryPercent={
            <SecondaryPercent
              isNarrow={isNarrow}
              isSuperNarrow={isSuperNarrow}
              isHidden={isHidden}
              locale={locale}
              percentage={percentage}
            />
          }
          secondaryTotal={
            <SecondaryTotal
              locale={locale}
              isNarrow={isNarrow}
              isSuperNarrow={isSuperNarrow}
              isHidden={isHidden}
              count={gauge.count}
            />
          }
        />
      )}
      {secondaryValue === "evolution" && (
        <BracketValue
          isNarrow={isNarrow}
          isHidden={isHidden}
          value={getDisplayedBracketValue(
            percentage,
            gauge.count,
            mainUnit,
            locale
          )}
        />
      )}
    </Root>
  );
};

export default HorizontalBarChartGauge;
